import React, { useEffect } from 'react';
import useForm from '~/app/modules/form/useForm';
import { IssueClosePayload } from '~/types';
import classes from './style.module.scss';
import useTranslation from '~/app/hooks/useTranslation';
import { useCostCategories, useCustomers, useIssueSortingTypes, useProjects, useWorkOrderHPCodes, useWorkTypes } from '~/app/data/statuses/issue';
import Select from '~/app/modules/form/Select';
import { useStoreActions, useStoreState } from '~/store';
import WorkHoursInput from '~/app/modules/form/WorkHoursInput';
import Text from '~/app/modules/form/Text';

export type WorkOrderComponent = React.FC;

const WorkOrder: WorkOrderComponent = () => {
    const { t } = useTranslation('issue');

    const { values: formValues, fieldEvents: formFieldEvents } = useForm<IssueClosePayload>({ formName: 'closeIssue' });
    
    const machine = useStoreState(state => state.machine.data);

    const isWorkOrderFormActive = useStoreState(state => state.issue.isWorkOrderFormActive);
    const sortingTypes = useStoreState(state => state.issue.sortingTypes);
    const issueSortingTypes = useIssueSortingTypes(sortingTypes);
    const displayTypeInIssues = useStoreState(state => state.machine.displayTypeInIssues);

    const HPCodes = useStoreState(state => state.issue.HPCodes);
    const issueHpCodes = useWorkOrderHPCodes(HPCodes);
    const isHPCodeRequired = useStoreState(state => state.issue.isHPCodeRequired);

    const workTypes = useStoreState(state => state.issue.workTypes);
    const workorderTypes = useWorkTypes(workTypes);

    const costCategories = useStoreState(state => state.issue.costCategories);
    const workorderCostCategories = useCostCategories(costCategories);

    const customers = useStoreState(state => state.issue.customers);
    const workorderCustomers = useCustomers(customers);
    const displayCustomer = useStoreState(state => state.issue.displayCustomer);
    const setDisplayCustomer = useStoreActions(action => action.issue.setDisplayCustomer);

    const projects = useStoreState(state => state.projects.list);
    const workorderProjects = useProjects(projects);
    const displayProject = useStoreState(state => state.issue.displayProject);
    const setDisplayProject = useStoreActions(action => action.issue.setDisplayProject);

    const selectedCategory = costCategories.find(({ id }) => Number(id) === Number(formValues?.costCategory));
    const displayOverTime50 = isWorkOrderFormActive && !!selectedCategory?.costData?.overtime1Percentage;
    const displayOverTime100 = isWorkOrderFormActive && !!selectedCategory?.costData?.overtime2Percentage;

    useEffect(() => {
        const selectedCategoryId = Number(formValues?.costCategory);
        const selectedCategory = costCategories.find(({ id }) => Number(id) === selectedCategoryId);
        setDisplayCustomer(isWorkOrderFormActive && !!selectedCategory?.displayCustomer);
    }, [costCategories, formValues?.costCategory, isWorkOrderFormActive, setDisplayCustomer]);

    useEffect(() => {
        const selectedCustomerId = Number(formValues?.customer);
        const selectedCustomer = customers.find(({ id }) => Number(id) === selectedCustomerId);
        setDisplayProject(isWorkOrderFormActive && !!selectedCustomer?.displayProject);
    }, [customers, formValues?.customer, isWorkOrderFormActive, setDisplayCustomer, setDisplayProject]);

    return (
        <div className={classes['workorder-container']}>
            
            {displayTypeInIssues && <Select
                className={`${classes['input-container']}`}
                name="sortingType"
                placeholder={t('sortingType.placeholder')}
                options={issueSortingTypes}
                icon={'keyboard_arrow_down'}
                onChange={formFieldEvents.onChange}
                defaultValue={formValues.sortingType}
                showEmptyOption={true}
                emptyOptionValue={0}
            />}
            {isHPCodeRequired && <Select
                className={`${classes['input-container']}`}
                name="hpCode"
                placeholder={t('hpCode.placeholder')}
                options={issueHpCodes}
                icon={'keyboard_arrow_down'}
                onChange={formFieldEvents.onChange}
                defaultValue={formValues.hpCode}
                showEmptyOption={true}
                emptyOptionValue={0}
            />}
            <Select
                className={`${classes['input-container']}`}
                name="workType"
                placeholder={t('workType.placeholder')}
                options={workorderTypes}
                icon={'keyboard_arrow_down'}
                onChange={formFieldEvents.onChange}
                defaultValue={formValues.workType}
                showEmptyOption={true}
                emptyOptionValue={0}
            />
            <Select
                className={`${classes['input-container']}`}
                name="costCategory"
                placeholder={t('costCategory.placeholder')}
                options={workorderCostCategories}
                icon={'keyboard_arrow_down'}
                onChange={formFieldEvents.onChange}
                defaultValue={formValues.costCategory}
                showEmptyOption={true}
                emptyOptionValue={0}
            />
            {displayCustomer && <Select
                className={`${classes['input-container']}`}
                name="customer"
                placeholder={t('customer.placeholder')}
                options={workorderCustomers}
                icon={'keyboard_arrow_down'}
                onChange={formFieldEvents.onChange}
                defaultValue={formValues.customer}
                showEmptyOption={true}
                emptyOptionValue={0}
            />}
            {displayProject && <Select
                className={`${classes['input-container']}`}
                name="project"
                placeholder={t('project.placeholder')}
                options={workorderProjects}
                icon={'keyboard_arrow_down'}
                onChange={formFieldEvents.onChange}
                defaultValue={formValues.project}
                showEmptyOption={true}
                emptyOptionValue={0}
            />}
            {displayOverTime50 && <WorkHoursInput
                placeholder={t('overTime50.placeholder', { overTime50: `${selectedCategory?.costData?.overtime1Percentage}%` })}
                name="overTime50"
                defaultValue={formValues.overTime50}
                {...formFieldEvents}
            />}
            {displayOverTime100 && <WorkHoursInput
                placeholder={t('overTime100.placeholder', { overTime100: `${selectedCategory?.costData?.overtime2Percentage}%` })}
                name="overTime100"
                defaultValue={formValues.overTime100}
                {...formFieldEvents}
            />}
            {!!machine.isOperatingTimeVisible && <Text
                className={`${classes['input-container']}`}
                inputClassName={`${classes['input']}`}
                name="operatingTime"
                type="number"
                placeholder={t('operatingTime.placeholder')}
                autoComplete="off"
                min={0}
                {...formFieldEvents}
            />}
            {!!machine.isOperatingCountVisible && <Text
                className={`${classes['input-container']}`}
                inputClassName={`${classes['input']}`}
                name="operatingCount"
                type="number"
                placeholder={t('operatingCount.placeholder')}
                autoComplete="off"
                min={0}
                {...formFieldEvents}
            />}
            <WorkHoursInput
                placeholder={t('ordinaryHours.placeholder')}
                name="ordinaryHours"
                defaultValue={formValues.ordinaryHours}
                {...formFieldEvents}
            />
            
        </div>
    );
};

export default WorkOrder;